import { useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Check, X, Ellipsis } from 'lucide-react';
import type {
  BulkEditBlock as BulkEditBlockType,
  BulkEditRow as BulkEditRowType,
} from '@netsuite-ai/types';
import { BulkEditRow } from './BulkEditRow';
import { BulkEditDrawer } from './BulkEditDrawer';
import Modal from '../../Modal';
import { resolveBlock } from '../../../lib/chat-stream'; // ← add
import { useAuthStore } from '../../../store/auth.store';
import Menus from '../../Menus';

interface Props {
  block: BulkEditBlockType;
}

export function BulkEditBlock({ block }: Props) {
  const [rows, setRows] = useState<BulkEditRowType[]>(block.rows);
  const [appliedCount, setAppliedCount] = useState<number | null>(
    block.appliedCount ?? null,
  );
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const role = useAuthStore((s) => s.role);
  const isReadOnly = role === 'support_readonly';

  const visibleRows = rows.filter((r) => r.status !== 'denied');
  const approvedCount = rows.filter((r) => r.status === 'approved').length;
  const pendingCount = rows.filter((r) => r.status === 'pending').length;
  const deniedCount = rows.filter((r) => r.status === 'denied').length;

  const rowVirtualizer = useVirtualizer({
    count: visibleRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 65,
    overscan: 2,
  });

  function setRowStatus(id: string, status: BulkEditRowType['status']) {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
  }

  function handleRowSave(id: string, fields: Record<string, string>) {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, fields } : r)));
  }

  function handleCancel() {
    setRows(block.rows.map((r) => ({ ...r, status: 'pending' as const })));
  }

  const approveAll = () =>
    setRows((prev) => prev.map((r) => ({ ...r, status: 'approved' as const })));
  const denyAll = () =>
    setRows((prev) => prev.map((r) => ({ ...r, status: 'denied' as const })));

  async function handleSave() {
    const approved = rows.filter((r) => r.status === 'approved');
    setBusy(true);
    await resolveBlock(block.id, 'approve', { rows: approved });
    setBusy(false);
    setAppliedCount(approved.length);
  }

  return (
    <Modal>
      <div className="my-2 w-full overflow-hidden rounded border border-neutral-200 bg-white shadow-sm">
        {/* Header */}
        <div className="border-b border-neutral-100 px-4 py-3">
          <div className="flex justify-between">
            <p className="text-sm font-bold text-neutral-900">{block.title}</p>

            {!isReadOnly && (
              <>
                {/* Wider screens: inline action buttons */}
                <div className="text-ink hidden items-center justify-between gap-4 font-medium sm:flex">
                  <button
                    onClick={approveAll}
                    className="bg-ink/8 flex items-center gap-2 rounded-sm px-3 py-1"
                  >
                    <Check size={16} />
                    Approve all
                  </button>
                  <button
                    onClick={denyAll}
                    className="bg-ink/8 flex items-center gap-2 rounded-sm px-3 py-1"
                  >
                    <X size={16} />
                    Deny all
                  </button>
                </div>

                {/* Mobile: ellipsis menu */}
                <div className="sm:hidden">
                  <Menus>
                    <Menus.Toggle id="bulk-actions">
                      <Ellipsis className="h-5 w-5" />
                    </Menus.Toggle>
                    <Menus.List id="bulk-actions">
                      <Menus.Button
                        icon={<Check size={16} />}
                        onClick={approveAll}
                      >
                        Approve all
                      </Menus.Button>
                      <Menus.Button icon={<X size={16} />} onClick={denyAll}>
                        Deny all
                      </Menus.Button>
                    </Menus.List>
                  </Menus>
                </div>
              </>
            )}
          </div>
          {approvedCount > 0 && (
            <p className="mt-0.5 text-xs text-neutral-500">
              {approvedCount} of {rows.length} approved
            </p>
          )}
        </div>

        {/* Row list */}
        <div ref={scrollRef} className="max-h-100 overflow-y-auto">
          <div
            className="relative"
            style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
          >
            {rowVirtualizer.getVirtualItems().map((item) => {
              const row = visibleRows[item.index];
              return (
                <div
                  key={item.key}
                  data-index={item.index}
                  ref={rowVirtualizer.measureElement}
                  className="absolute top-0 left-0 w-full border-b border-neutral-100"
                  style={{ transform: `translateY(${item.start}px)` }}
                >
                  <BulkEditRow
                    row={row}
                    onApprove={() =>
                      setRowStatus(
                        row.id,
                        row.status === 'approved' ? 'pending' : 'approved',
                      )
                    }
                    onDeny={() =>
                      setRowStatus(
                        row.id,
                        row.status === 'denied' ? 'pending' : 'denied',
                      )
                    }
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}

        {/* Footer */}
        {!isReadOnly && (
          <div className="flex flex-col gap-3 border-t border-neutral-100 bg-neutral-100 px-4 py-4">
            <div className="flex flex-wrap gap-4 text-xs text-neutral-500">
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-green-500" />
                {approvedCount} approved
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-neutral-400" />
                {pendingCount} pending
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-red-400" />
                {deniedCount} denied
              </span>
            </div>
            {appliedCount !== null ? (
              <p className="flex w-fit items-center gap-1.5 text-sm font-medium text-green-700 dark:text-green-400">
                <Check size={16} />
                Applied {appliedCount} record{appliedCount === 1 ? '' : 's'}
              </p>
            ) : (
              <button
                onClick={handleSave}
                disabled={approvedCount === 0 || busy}
                className="bg-danger w-fit rounded px-4 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-40"
              >
                Apply approved
              </button>
            )}
          </div>
        )}
      </div>

      {/* One modal window per row — only the open one renders a portal */}
      {rows.map((row) => (
        <Modal.Window key={row.id} name={row.id}>
          <BulkEditDrawer row={row} onSave={handleRowSave} />
        </Modal.Window>
      ))}
    </Modal>
  );
}
